import config from '../config';

const guestTokenKey = `${config.tokenKey}_guest_room_hire`;
const guestEmailKey = `${config.tokenKey}_guest_room_hire_email`;
const guestExpiresAtKey = `${config.tokenKey}_guest_room_hire_expires_at`;
const guestDraftKey = `${config.tokenKey}_guest_room_hire_draft`;
const choreographySubjectTokenKey = `${config.tokenKey}_choreo_subject`;
const choreographySubjectExpiresAtKey = `${config.tokenKey}_choreo_subject_expires_at`;
const choreographyRefreshTokenKey = `${config.tokenKey}_choreo_refresh`;

const getSessionStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch (error) {
    return null;
  }
};

const getLocalStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
};

// One-time migration keeps existing signed-in tabs working while moving bearer tokens out of localStorage.
const migrateLegacyAccessToken = () => {
  const sessionStorage = getSessionStorage();
  const sessionToken = sessionStorage?.getItem(config.tokenKey);

  if (sessionToken) {
    return sessionToken;
  }

  const localStorage = getLocalStorage();
  const legacyToken = localStorage?.getItem(config.tokenKey);

  if (legacyToken && sessionStorage) {
    sessionStorage.setItem(config.tokenKey, legacyToken);
    localStorage.removeItem(config.tokenKey);
  }

  return legacyToken || null;
};

export const getAccessToken = () => migrateLegacyAccessToken();

export const setAccessToken = (token) => {
  if (!token) {
    clearAccessToken();
    return;
  }

  const sessionStorage = getSessionStorage();
  const localStorage = getLocalStorage();

  sessionStorage?.setItem(config.tokenKey, token);
  localStorage?.removeItem(config.tokenKey);
};

export const clearAccessToken = () => {
  const sessionStorage = getSessionStorage();
  sessionStorage?.removeItem(config.tokenKey);
  sessionStorage?.removeItem('studioaeye_choreography_access_token');
  sessionStorage?.removeItem('studioaeye_choreography_access_token_expires_at');
  getLocalStorage()?.removeItem(config.tokenKey);
};

// Set only by the Core Connect choreo-local OIDC+PKCE exchange. The ordinary
// StudioAEye session token is deliberately never used as a Choreo audience token.
export const getChoreographySubjectToken = () => {
  const storage = getSessionStorage(); const token = storage?.getItem(choreographySubjectTokenKey) || null;
  const expiresAt = Number(storage?.getItem(choreographySubjectExpiresAtKey) || 0);
  if (!token || !expiresAt || expiresAt <= Date.now() + 30_000) {
    storage?.removeItem(choreographySubjectTokenKey); storage?.removeItem(choreographySubjectExpiresAtKey); return null;
  }
  return token;
};
export const setChoreographySubjectToken = (token, expiresAt = Date.now() + (15 * 60 * 1000)) => {
  const storage = getSessionStorage(); const expiry = Number(expiresAt);
  if (token && Number.isFinite(expiry) && expiry > Date.now()) {
    storage?.setItem(choreographySubjectTokenKey, token); storage?.setItem(choreographySubjectExpiresAtKey, String(expiry));
  } else {
    storage?.removeItem(choreographySubjectTokenKey); storage?.removeItem(choreographySubjectExpiresAtKey);
  }
};
export const getChoreographyRefreshToken = () => getSessionStorage()?.getItem(choreographyRefreshTokenKey) || null;
export const setChoreographyRefreshToken = (token) => {
  if (token) getSessionStorage()?.setItem(choreographyRefreshTokenKey, token);
  else getSessionStorage()?.removeItem(choreographyRefreshTokenKey);
};
export const clearChoreographySubjectToken = () => {
  const storage = getSessionStorage(); storage?.removeItem(choreographySubjectTokenKey);
  storage?.removeItem(choreographySubjectExpiresAtKey); storage?.removeItem(choreographyRefreshTokenKey);
};
export const buildChoreographyAuthHeaders = (headers = {}) => {
  const token = getChoreographySubjectToken();
  if (!token) { const error = new Error('The external Core Choreo app-launch token is unavailable.'); error.code = 'CHOREO_SUBJECT_TOKEN_UNAVAILABLE'; throw error; }
  return { ...headers, Authorization: `Bearer ${token}` };
};

export const buildAuthHeaders = (headers = {}) => {
  const token = getAccessToken();

  return token
    ? { ...headers, Authorization: `Bearer ${token}` }
    : { ...headers };
};

export const getGuestSession = () => {
  const sessionStorage = getSessionStorage();

  if (!sessionStorage) {
    return null;
  }

  const token = sessionStorage.getItem(guestTokenKey);
  const email = sessionStorage.getItem(guestEmailKey);
  const expiresAt = sessionStorage.getItem(guestExpiresAtKey);

  if (!token) {
    return null;
  }

  if (expiresAt && new Date(expiresAt).getTime() <= Date.now()) {
    clearGuestSession();
    return null;
  }

  return {
    token,
    email: email || '',
    expiresAt: expiresAt || '',
  };
};

export const setGuestSession = ({ token, email, expiresAt }) => {
  const sessionStorage = getSessionStorage();

  if (!sessionStorage) {
    return;
  }

  if (!token) {
    clearGuestSession();
    return;
  }

  sessionStorage.setItem(guestTokenKey, token);

  if (email) {
    sessionStorage.setItem(guestEmailKey, email);
  } else {
    sessionStorage.removeItem(guestEmailKey);
  }

  if (expiresAt) {
    sessionStorage.setItem(guestExpiresAtKey, expiresAt);
  } else {
    sessionStorage.removeItem(guestExpiresAtKey);
  }
};

export const clearGuestSession = () => {
  const sessionStorage = getSessionStorage();

  sessionStorage?.removeItem(guestTokenKey);
  sessionStorage?.removeItem(guestEmailKey);
  sessionStorage?.removeItem(guestExpiresAtKey);
};

export const getGuestHireDraft = () => {
  const sessionStorage = getSessionStorage();
  const raw = sessionStorage?.getItem(guestDraftKey);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (error) {
    sessionStorage?.removeItem(guestDraftKey);
    return null;
  }
};

export const setGuestHireDraft = (draft) => {
  const sessionStorage = getSessionStorage();

  if (!sessionStorage) {
    return;
  }

  if (!draft || typeof draft !== 'object') {
    sessionStorage.removeItem(guestDraftKey);
    return;
  }

  sessionStorage.setItem(guestDraftKey, JSON.stringify(draft));
};

export const clearGuestHireDraft = () => {
  getSessionStorage()?.removeItem(guestDraftKey);
};
